"use client";
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, Plus, Minus, Bell, Globe } from "lucide-react";
import Logo from "../atoms/Logo";
import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";

export default function AdminHeader() {
  const [showProfile, setShowProfile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);

  const notifications = [
    {
      id: 1,
      title: "New Job Application",
      message: "A new candidate applied for Frontend Developer",
      time: "2 minutes ago",
      read: false,
    },
    {
      id: 2,
      title: "Company Profile Updated",
      message: "Your company profile was updated successfully",
      time: "1 hour ago",
      read: true,
    },
    {
      id: 3,
      title: "Job Post Approved",
      message: "Your job post has been approved",
      time: "Yesterday",
      read: true,
    },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
  const handleClickOutside = () => {
    setShowNotification(false);
  };

  if (showNotification) {
    document.addEventListener("click", handleClickOutside);
  }

  return () => {
    document.removeEventListener("click", handleClickOutside);
  };
}, [showNotification]);



  const currentLang = location.pathname.startsWith("/id") ? "id" : "en";
  const { hrProfile, fetchHrProfile } = useCompanyProfileStore();
  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    fullName: "",
    jobTitle: "",
    email: "",
  });

  const switchLanguage = (lang: "en" | "id") => {
    if (lang === currentLang) return;
    const newPath = location.pathname.replace(/^\/(id|en)/, `/${lang}`);
    navigate(newPath);
  };
  const langPrefix = currentLang === "id" ? "/id" : "/en";

  // ===== ACTIVE CHECKER =====
  const isActive = (path: string) => {
    return location.pathname.startsWith(path)
      ? "text-primary font-bold"
      : "text-black";
  };

  // ===== MULTILINGUAL LABELS =====
  const t = {
    jobPost: currentLang === "en" ? "Jobs Post" : "Lowongan Kerja",
    companyProfile: currentLang === "en" ? "Dashboard" : "Dashboard",
    userProfile: currentLang === "en" ? "User Profile" : "Profil Pengguna",
  };
  const navLabels = {
    id: {
      blog: "Blog",
      news: "Berita",
      events: "Acara",
      tips: "Tips Karier",
      service: "Layanan Kami",
      headhunter: "Head Hunter",
      recruitment: "Rekrutmen Talent",
      hr: "Kepatuhan SDM",
    },
    en: {
      blog: "Blog",
      news: "News",
      events: "Events",
      tips: "Talent Tips",
      service: "Our Service",
      headhunter: "Head Hunter",
      recruitment: "Talent Recruitment",
      hr: "HR Compliance",
    },
  };
  const navItems = [
    {
      label: navLabels[currentLang].blog,
      to: `/blog/news`,
      subItems: [
        { label: navLabels[currentLang].news, to: `${langPrefix}/blog/news` },
        { label: navLabels[currentLang].events, to: `${langPrefix}/blog/events` },
        // { label: navLabels[currentLang].tips, to: `${langPrefix}/blog/talent-tips` },
      ],
    },
    { label: navLabels[currentLang].service, to: `/our-service` },
  ];

  const [avatar, setAvatar] = useState<string>("https://placehold.co/200");

  const handleLogout = () => {
    localStorage.removeItem("auth-storage");
    window.location.href = "/";
  };

  // --- AUTO FETCH on language change ---
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchHrProfile();
      setLoading(false);
    };
    load();
  }, [currentLang, fetchHrProfile]);

  useEffect(() => {
    if (!hrProfile) return;

    setForm({
      fullName: hrProfile.name || "",
      jobTitle: hrProfile.job_title || "",
      email: hrProfile.email || "",
    });

    if (hrProfile.photo) {
      setAvatar(hrProfile.photo);
    }
  }, [hrProfile]);


  const toggleSubMenu = (label: string) => {
    setOpenSubMenu(openSubMenu === label ? null : label);
  };

  return (
    <header className="fixed w-full top-0 z-50 transition-all duration-300 bg-white shadow-md">
      <div className="container-banner py-4 flex justify-between items-center sm:px-16 lg:px-20 xl:px-28 max-w-[1920px] mx-auto px-6">

        {/* ==== Logo + Menu ==== */}
        <div className="flex items-center gap-4">
          <Link to={`/${currentLang}/homepage`}>
            <Logo color="black" />
          </Link>

        </div>

        <div className="hidden lg:flex items-center gap-10 flex-1 relative">
          <nav className="flex gap-6 lg:ml-16 xl:ml-28 relative">

            {navItems.map((item) => {
              const fullPath = `/${currentLang}${item.to}`;
              const active = location.pathname.startsWith(fullPath); // rename supaya tidak conflict dengan fungsi luar

              const textColor = active ? "text-primary" : "text-black";
              const hoverColor = "hover:text-primary";

              return (
                <div key={item.to} className="relative group">
                  <Link
                    to={fullPath}
                    className={`font-semibold flex items-center gap-1 transition-colors ${textColor} ${hoverColor}`}
                  >
                    {item.label}
                    {item.subItems && (
                      <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                    )}
                  </Link>

                  {item.subItems && (
                    <div className="absolute left-0 top-full mt-2 bg-white text-black rounded-xl shadow-lg opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 w-48 py-2 z-50">
                      {item.subItems.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className="block px-4 py-2 hover:bg-primary hover:text-white text-sm"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <nav className="hidden lg:flex gap-8 font-semibold">
              <Link
                to={`/${currentLang}/admin/job-post`}
                className={`${isActive(`/${currentLang}/admin/job-post`)} hover:text-primary`}
              >
                {t.jobPost}
              </Link>

              <Link
              to={`/${currentLang}/admin/company-profile`}
              className={`${isActive(`/${currentLang}/admin/company-profile`)} hover:text-primary`}
            >
              {t.companyProfile}
            </Link>
            </nav>

          </nav>
          {/* ==== Right Icons ==== */}
          <div className="ml-auto flex items-center gap-4 relative">

            {/* === Language Selector === */}
            <div className="flex items-center gap-1 cursor-pointer">
              <Globe size={18} className="text-gray-600" />
              <select
                value={currentLang}
                onChange={(e) => switchLanguage(e.target.value as "en" | "id")}
                className="bg-transparent font-semibold text-gray-700"
              >
                <option value="en">EN</option>
                <option value="id">ID</option>
              </select>
            </div>

            {/* Notification */}
            {/* <button
              className="relative"
              onClick={(e) => {
                e.stopPropagation();
                setShowNotification(!showNotification);
              }}
            >
              <Bell size={20} className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button> */}

            {showNotification && (
              <div className="absolute right-0 top-10 w-80 bg-white shadow-lg rounded-xl border z-50">
                <div className="p-4 border-b">
                  <p className="font-semibold text-gray-900">Notifications</p>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">
                      No notifications
                    </p>
                  ) : (
                    notifications.map((item) => (
                      <div
                        key={item.id}
                        className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 ${!item.read ? "bg-blue-50" : ""
                          }`}
                      >
                        <p className="text-sm font-semibold text-gray-800">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {item.message}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {item.time}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                {/* <div className="p-3 text-center">
                  <button className="text-sm text-primary hover:underline">
                    View all notifications
                  </button>
                </div> */}
              </div>
            )}



            {/* Profile */}
            <div className="relative">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center gap-2"
              >
                <img
                  src={avatar}
                  alt="User Avatar"
                  className="w-10 h-10 rounded-full object-cover border"
                />
                <ChevronDown className="text-gray-600" size={18} />
              </button>

              {/* === Profile Dropdown === */}
              {showProfile && (
                <div className="absolute right-0 mt-3 w-72 bg-white shadow-lg rounded-xl border p-4 z-50">
                  <div className="flex items-center gap-3">
                    <img
                      src={avatar}
                      alt="User Avatar"
                      className="w-10 h-10 rounded-full object-cover border"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">{form.fullName}</p>
                      <p className="text-xs text-gray-500">{form.jobTitle}</p>
                      <p className="text-xs text-gray-500">{form.email}</p>
                    </div>
                  </div>

                  {/* Navigation (mobile only) */}
                  <div className="text-sm flex flex-col gap-3 mt-3">
                    <Link to={`/${currentLang}/admin/user-profile`} className="hover:text-primary">
                      {t.userProfile}
                    </Link>
                  </div>

                  <hr className="my-3" />

                  <button onClick={handleLogout}  className="text-red-500 font-semibold hover:text-red-600">
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="flex gap-4">
                      {/* <button
              className="relative md:hidden"
              onClick={(e) => {
                e.stopPropagation();
                setShowNotification(!showNotification);
              }}
            >
              <Bell size={20} className="text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button> */}

            {showNotification && (
              <div className="absolute right-0 md:hidden top-10 w-80 bg-white shadow-lg rounded-xl border z-50">
                <div className="p-4 border-b">
                  <p className="font-semibold text-gray-900">Notifications</p>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">
                      No notifications
                    </p>
                  ) : (
                    notifications.map((item) => (
                      <div
                        key={item.id}
                        className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 ${!item.read ? "bg-blue-50" : ""
                          }`}
                      >
                        <p className="text-sm font-semibold text-gray-800">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {item.message}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {item.time}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                {/* <div className="p-3 text-center">
                  <button className="text-sm text-primary hover:underline">
                    View all notifications
                  </button>
                </div> */}
              </div>
            )}

          <div className="relative md:hidden">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-2"
            >
              <img
                src={avatar}
                alt="User Avatar"
                className="w-10 h-10 rounded-full object-cover border"
              />
              <ChevronDown className="text-gray-600" size={18} />
            </button>

            {/* === Profile Dropdown === */}
            {showProfile && (
              <div className="absolute right-0 mt-3 w-72 bg-white shadow-lg rounded-xl border p-4 z-50">
                <div className="flex items-center gap-3">
                  <img
                    src={avatar}
                    alt="User Avatar"
                    className="w-10 h-10 rounded-full object-cover border"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">{form.fullName}</p>
                    <p className="text-xs text-gray-500">{form.jobTitle}</p>
                    <p className="text-xs text-gray-500">{form.email}</p>
                  </div>
                </div>

                <div className="text-sm flex flex-col gap-3 mt-3">
                  <Link to={`/${currentLang}/admin/user-profile`} className="hover:text-primary">
                    {t.userProfile}
                  </Link>
                </div>

                <hr className="my-3" />

                <button onClick={handleLogout}  className="text-red-500 font-semibold hover:text-red-600">
                  Log Out
                </button>
              </div>
            )}
          </div>

          <button className="lg:hidden" onClick={() => setIsOpen(true)}>
            <Menu className={`w-6 h-6 text-black`} />
          </button>

        </div>

      </div>

      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        onClick={() => setIsOpen(false)}
      />

      <div
        className={`fixed top-0 left-0 h-full w-3/4 bg-white shadow-lg z-50 transform transition-transform duration-300 lg:hidden ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="py-4 px-6 flex flex-col gap-4 h-full overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <Logo color="black" />
            <button onClick={() => setIsOpen(false)}>
              <X className="w-6 h-6 text-black" />
            </button>
          </div>

          <nav className="flex flex-col gap-2">
            {navItems.map((item) => (
              <div key={item.to}>
                <div
                  className="flex justify-between items-center py-2 cursor-pointer"
                  onClick={() =>
                    item.subItems ? toggleSubMenu(item.label) : setIsOpen(false)
                  }
                >
                  <Link
                    to={`/${currentLang}${item.to}`}
                    className="font-semibold text-black hover:text-secondary"
                  >
                    {item.label}
                  </Link>
                  {item.subItems && (
                    <span>
                      {openSubMenu === item.label ? (
                        <Minus className="w-4 h-4 text-black" />
                      ) : (
                        <Plus className="w-4 h-4 text-black" />
                      )}
                    </span>
                  )}
                </div>

                {item.subItems && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openSubMenu === item.label ? "max-h-40" : "max-h-0"
                      }`}
                  >
                    {item.subItems.map((sub) => (
                      <Link
                        key={sub.to}
                        to={`/${currentLang}${sub.to}`}
                        onClick={() => setIsOpen(false)}
                        className="block pl-6 py-2 text-sm text-gray-700 hover:text-secondary"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
                  
              <Link
                to={`/${currentLang}/admin/job-post`}
                className={`${isActive(`/${currentLang}/admin/job-post`)} font-semibold hover:text-primary`}
              >
                {t.jobPost}
              </Link>
          </nav>
          

              <Link
              to={`/${currentLang}/admin/company-profile`}
              className={`${isActive(`/${currentLang}/admin/company-profile`)} font-semibold hover:text-primary`}
            >
              {t.companyProfile}
            </Link>
          <div className="flex gap-2 text-sm font-medium">
            <button
              onClick={() => switchLanguage("id")}
              className={`font-semibold transition-colors text-primary ${currentLang === "id"}`}
            >
              ID
            </button>

            <div className={"text-black"}>|</div>

            <button
              onClick={() => switchLanguage("en")}
              className={`font-semibold transition-colors text-primary ${currentLang === "en"}`}
            >
              EN
            </button>
          </div>
          <hr className="my-4 border-gray-300" />
        </div>
      </div>
    </header>
  );
}
